import { createContext, useCallback, useContext, useMemo, useState } from "react";
import useSWR from "swr";
import type { ReactNode } from "react";

import { useSessionUser } from "@/hooks/useSessionUser";
import { DEFAULT_MARKETING_PERMISSIONS, type MarketingPermissions } from "@/hooks/usePermissions";
import { DEFAULT_CAPABILITIES } from "@/types/capabilities";
import { fetcher } from "@/lib/fetcher";

type CapabilityContextValue = {
  tier: string | null;
  capabilities: Record<string, boolean>;
  openUpgradeModal: (feature?: string, message?: string) => void;
  hasFullAccess: boolean;
  ready: boolean;
};

type UpgradeState = {
  open: boolean;
  feature?: string;
  capability?: string;
  message?: string;
};

const CapabilityContext = createContext<CapabilityContextValue | null>(null);

const MANAGER_PRO_FLAGS = [
  "canUseManagerAgent",
  "canUseManagerAI",
  "canUseManagerWorkflows",
  "canUseManagerTimeline",
  "canUseManagerTimelineAdvanced",
  "canUseManagerInsights",
  "canUseManagerTasks",
  "canUseManagerAutopilot",
  "canUseManagerMCP",
  "canUseNextActionsAI",
  "canUseEventFinanceTools",
  "canUseLedgerAutomation",
  "canUseLedgerSync",
  "canUseAccountingAutomations",
  "canUseSettlementPreview",
  "canUseCashflowAutomation",
  "canUseEventImportAutomation",
  "canUseRoutingIntegration",
  "canApproveHitl",
  "canRejectHitl",
  "canAssignHitl",
  "canUseAdvancedHitl",
  "canUseManagerAIInsights",
] as const;

const buildBaselineCapabilities = () => {
  const baseline = { ...DEFAULT_CAPABILITIES };
  MANAGER_PRO_FLAGS.forEach((flag) => {
    if (!(flag in baseline)) {
      baseline[flag] = flag === "canUseManagerAgent";
    }
  });
  return baseline;
};

export function CapabilityProvider({ children }: { children: React.ReactNode }) {
  const { capabilities: sessionCapabilities, tier, organizationTier, session } = useSessionUser();
  const [upgradeState, setUpgradeState] = useState<UpgradeState>({ open: false });
  const shouldFetchMarketingPerms = Boolean(sessionCapabilities);
  const { data: marketingPermissions } = useSWR<MarketingPermissions>(
    shouldFetchMarketingPerms ? "/auth/permissions/marketing" : null,
    fetcher,
    { revalidateOnFocus: false, fallbackData: DEFAULT_MARKETING_PERMISSIONS },
  );

  const openUpgradeModal = useCallback((feature?: string, message?: string) => {
    setUpgradeState({ open: true, feature, message });
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeState((prev) => ({ ...prev, open: false }));
  }, []);

  const marketingCapabilityMap = useMemo(() => {
    const perms = marketingPermissions ?? DEFAULT_MARKETING_PERMISSIONS;
    return {
      marketing_can_schedule: perms.canSchedule,
      marketing_can_publish_now: perms.canPublishNow,
      marketing_can_edit_assets: perms.canEditAssets,
      marketing_can_manage_campaigns: perms.canManageCampaigns,
      marketing_can_use_automation: perms.canUseAutomation,
      instagram_publish: perms.platform.instagram,
      tiktok_publish: perms.platform.tiktok,
      facebook_publish: perms.platform.facebook,
      youtube_publish: perms.platform.youtube,
    };
  }, [marketingPermissions]);

  const { resolvedCapabilities, hasFullAccess } = useMemo(() => {
    const normalizedTier = typeof tier === "string" ? tier.toUpperCase() : "FREE";
    const normalizedOrgTier = typeof organizationTier === "string" ? organizationTier.toUpperCase() : "FREE";
    const fullAccess = ["PRO", "AGENCY", "OWNER"].includes(normalizedTier) || ["PRO", "AGENCY", "OWNER"].includes(normalizedOrgTier);
    let merged = buildBaselineCapabilities();
    if (fullAccess) {
      merged = Object.keys(merged).reduce(
        (acc, key) => ({
          ...acc,
          [key]: true,
        }),
        {} as Record<string, boolean>,
      );
    }
    if (sessionCapabilities && Object.keys(sessionCapabilities).length > 0) {
      merged = { ...merged, ...sessionCapabilities };
    }
    merged = { ...merged, ...marketingCapabilityMap };
    return { resolvedCapabilities: merged, hasFullAccess: fullAccess };
  }, [sessionCapabilities, tier, organizationTier, marketingCapabilityMap]);

  const contextValue = useMemo<CapabilityContextValue>(
    () => ({
      tier: tier ?? null,
      capabilities: resolvedCapabilities,
      openUpgradeModal,
      hasFullAccess,
      ready: Boolean(session),
    }),
    [resolvedCapabilities, tier, openUpgradeModal, hasFullAccess, session],
  );

  return (
    <CapabilityContext.Provider value={contextValue}>
      {children}
      {upgradeState.open && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md space-y-4 rounded-2xl border border-amber-400/30 bg-background p-6 text-foreground shadow-2xl">
            <div>
              <p className="text-xs uppercase tracking-wide text-primary-foreground dark:text-amber-200">Capability locked</p>
              <h3 className="text-2xl font-semibold text-foreground">Upgrade to unlock more</h3>
              {(upgradeState.feature || upgradeState.capability) && (
                <p className="text-sm text-muted-foreground">
                  {upgradeState.message ??
                    `The feature "${upgradeState.feature ?? upgradeState.capability}" lives in the Pro toolkit.`}
                </p>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              Enable workflow editing, global automation, HITL control, routing advisors, and CRM/MCP accelerators by upgrading to Pro.
            </p>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                className="flex-1 rounded-xl border border-amber-400/60 bg-amber-400/10 px-4 py-2 text-sm font-semibold text-primary-foreground dark:text-amber-100 transition hover:border-amber-300 hover:bg-amber-400/20"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    window.open("mailto:hello@artistascend.app?subject=ArtistAscend%20Pro%20Upgrade", "_blank");
                  }
                }}
              >
                Contact sales
              </button>
              <button
                type="button"
                onClick={closeUpgradeModal}
                className="flex-1 rounded-xl border border-border px-4 py-2 text-sm font-semibold text-foreground/70 transition hover:border-border hover:text-foreground"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}
    </CapabilityContext.Provider>
  );
}

export function useCapabilityContext() {
  const ctx = useContext(CapabilityContext);
  if (!ctx) {
    throw new Error("useCapabilityContext must be used within a CapabilityProvider");
  }
  return ctx;
}
